import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { useScrollToTopOnLoad } from "@/utils/hooksUtils";
import { validations } from "@/utils/validationUtils";
import { FieldData } from "@rc-component/form/es/interface";
import { App as AntApp, Card, Col, Form, Radio, Row } from "antd";
import { t } from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import HiddenInput from "../../../../../../common/components/form/components/HiddenInput";
import AntIcon, { type AntIconType } from "../../../../../../common/components/icons/AntIcon";
import { rowGutter } from "../../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../../common/types";
import { DashboardNoticePosition } from "../../../../../dashboard/enums";
import { CalcType } from "../../../../enums";
import { CALC_ROUTE_PATHS } from "../../../../paths";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { CalcNoticesIconView } from "../../../components/CalcNoticesIconView";
import { TravelInsuranceType } from "../../enums";
import { TravelCalc } from "../../types";
import { TravelCalcForm } from "./TravelCalcForm";

const resolvePageTitle = (type: TravelInsuranceType): string => {
  return type ? t("calc.travel.titles.insuranceType." + type) : t("calc.travel.titles.noSelectedInsurance");
};

const resolveInsuranceTypeIcon = (type: TravelInsuranceType): AntIconType => {
  switch (type) {
    case TravelInsuranceType.SHORT_TERM:
      return "schedule";
    case TravelInsuranceType.YEAR:
      return "global";
    case TravelInsuranceType.CANCELLATION:
      return "euro";
    default:
      return "schedule";
  }
};

interface Props {
  calcData?: TravelCalc;
  formErrors: FieldConstraintViolation[];
  setIsFormChanged: VoidFunction;
  onCalculationFormSubmit: (calcData: TravelCalc) => void;
  onResetCalculatorClick: (onSuccess: () => void) => void;
  goToCalcResults?: VoidFunction;
}

export const TravelCalcWrapper = (props: Props) => {
  useScrollToTopOnLoad();

  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<TravelCalc>();
  const insuranceType = Form.useWatch(["generalData", "insuranceType"], form);

  useEffect(() => {
    if (props.calcData) {
      form.setFieldsValue(props.calcData);
    } else {
      form.resetFields();
    }
  }, [props.calcData]);

  useEffect(() => {
    if (props.formErrors.length) {
      setErrorsToForm(form, "calc.travel.attrs", props.formErrors);
    }
  }, [props.formErrors]);

  const handleInsuranceTypeChange = (type: TravelInsuranceType) => {
    if (type === TravelInsuranceType.YEAR) {
      form.setFieldsValue({ generalData: { effectiveEndDate: undefined } });
    }

    if (
      (type === TravelInsuranceType.SHORT_TERM || type === TravelInsuranceType.YEAR) &&
      form.getFieldValue(["coveragesData", "cancellation"])
    ) {
      form.setFieldsValue({ coveragesData: { cancellationData: { tripPrice: undefined } } });
    }
  };

  const handleFormFieldsChange = (changedFields?: FieldData[]) => {
    if (changedFields?.length) {
      props.setIsFormChanged();
    }
  };

  const handleCalculationFormSubmit = () => {
    form
      .validateFields()
      .then(props.onCalculationFormSubmit)
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  return (
    <>
      <Form form={form} layout="vertical" name="travelCalcForm" onFieldsChange={handleFormFieldsChange}>
        <Card
          className="card-box card-box--inner-extra"
          title={<h2>{resolvePageTitle(insuranceType)}</h2>}
          extra={
            <>
              <CalcNoticesIconView position={DashboardNoticePosition.TRAVEL_CALC} />
              <Link to={CALC_ROUTE_PATHS.travelDraft.to}>
                <SecondaryButton icon={<AntIcon type="form" />}>{t("calc.actions.showDrafts")}</SecondaryButton>
              </Link>
            </>
          }
        >
          <Row gutter={rowGutter} justify="center">
            <Col span={12} className="center-align">
              <HiddenInput name="type" initialValue={CalcType.TRAVEL} />
              <Form.Item name={["generalData", "insuranceType"]} rules={[validations.notNull]}>
                <Radio.Group
                  block
                  optionType="button"
                  size="large"
                  buttonStyle="solid"
                  onChange={(e) => handleInsuranceTypeChange(e.target.value)}
                >
                  {Object.values(TravelInsuranceType).map((type) => (
                    <Radio.Button
                      value={type}
                      key={type}
                      style={{ height: 60, display: "flex", justifyContent: "center" }}
                    >
                      <AntIcon size="huge" type={resolveInsuranceTypeIcon(type)} />
                      <div style={{ fontSize: 16, lineHeight: "8px" }}>
                        {t("calc.travel.enums.insuranceType." + type)}
                      </div>
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <TravelCalcForm />
      </Form>

      <CalcNavigation
        onCalculate={handleCalculationFormSubmit}
        onResetCalculatorClick={() => props.onResetCalculatorClick(() => form.resetFields())}
        goToCalcResults={props.goToCalcResults}
      />
    </>
  );
};
